import React, { useState, useEffect } from 'react';
import { View, Text, Platform, Dimensions, Linking, Modal, Pressable, StyleSheet } from 'react-native';
import { NavigationContainer } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import Icon from 'react-native-vector-icons/MaterialIcons';
import * as SplashScreen from 'expo-splash-screen';
import { getColors, typography, getShadows } from '../styles/theme';
import { useTheme } from '../contexts/ThemeContext';
import { useMapReady, MapReadyProvider } from '../contexts/MapReadyContext';
import { ChatNotificationProvider } from '../contexts/ChatNotificationContext';

const { width: SCREEN_WIDTH } = Dimensions.get('window');
const isMobile = SCREEN_WIDTH < 768;

// Import screens
import LandingPage from '../screens/LandingPage';
import LoginScreen from '../screens/LoginScreen';
import RegisterScreen from '../screens/RegisterScreen';
import ForgotPasswordScreen from '../screens/ForgotPasswordScreen';
import AuthCallbackScreen from '../screens/AuthCallbackScreen';
import LinkAccountScreen from '../screens/LinkAccountScreen';
import CompleteProfileScreen from '../screens/CompleteProfileScreen';
import MapScreen from '../screens/MapScreen';
import ProfileScreen from '../screens/ProfileScreen';
import ChatScreen from '../screens/ChatScreen';
import ChatContainer from '../screens/ChatContainer';
import ChessClockScreen from '../screens/ChessClockScreen';
import ClubDashboardScreen from '../screens/ClubDashboardScreen';
import CreateEventScreen from '../screens/CreateEventScreen';
import SocialDraftReviewScreen from '../screens/SocialDraftReviewScreen';
import EditRecurringEventScreen from '../screens/EditRecurringEventScreen';
import ManageEventScreen from '../screens/ManageEventScreen';
import UserDetailScreen from '../screens/UserDetailScreen';
import ManageHotspotsScreen from '../screens/ManageHotspotsScreen';
import AddEditHotspotScreen from '../screens/AddEditHotspotScreen';
import MapPickerScreen from '../screens/MapPickerScreen';
import BulkImportHotspotsScreen from '../screens/BulkImportHotspotsScreen';
import EventDetailsScreen from '../screens/EventDetailsScreen';
import EventDiscoveryScreen from '../screens/EventDiscoveryScreen';
import EventCheckInScreen from '../screens/EventCheckInScreen';
import AttendanceAnalyticsScreen from '../screens/AttendanceAnalyticsScreen';

// Lazy-load scanner so expo-camera (createPermissionHook) is not loaded at startup - fixes web bundle
const CheckInScannerScreenLazy = React.lazy(() => import('../screens/CheckInScannerScreen'));

function CheckInScannerScreen(props) {
  return (
    <React.Suspense fallback={<View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}><Text>Loading...</Text></View>}>
      <CheckInScannerScreenLazy {...props} />
    </React.Suspense>
  );
}
import UnclaimedClubsScreen from '../screens/UnclaimedClubsScreen';
import UpgradeClubScreen from '../screens/UpgradeClubScreen';
import TournamentsScreen from '../screens/TournamentsScreen';
import TournamentWizardScreen from '../screens/TournamentWizardScreen';
import TournamentDetailScreen from '../screens/TournamentDetailScreen';
import TournamentControlScreen from '../screens/TournamentControlScreen';
import TournamentPlayerScreen from '../screens/TournamentPlayerScreen';
import TournamentProjectorScreen from '../screens/TournamentProjectorScreen';
import SettingsScreen from '../screens/SettingsScreen';
import PricingScreen from '../screens/PricingScreen';
import MembershipScreen from '../screens/MembershipScreen';
import HotspotDetailScreen from '../screens/HotspotDetailScreen';
import FriendsScreen from '../screens/FriendsScreen';
import AdminHotspotsListScreen from '../screens/AdminHotspotsListScreen';
import AdminHotspotsMapScreen from '../screens/AdminHotspotsMapScreen';
import ImportBatchesScreen from '../screens/ImportBatchesScreen';
import AdminClubApprovalsScreen from '../screens/AdminClubApprovalsScreen';
import AdminUsersScreen from '../screens/AdminUsersScreen';

// Import auth context
import { AuthProvider, useAuth } from '../services/AuthContext';
import LoadingAnimation from '../components/LoadingAnimation';
import AppSplashScreen from '../components/AppSplashScreen';

const Stack = createStackNavigator();
const Tab = createBottomTabNavigator();

// Ref for navigating from outside the screen tree (e.g. chat notifications). RN v5: plain ref, set by NavigationContainer.
export const navigationRef = { current: null };

// Splash screen duration in milliseconds
const SPLASH_DURATION = 2500;

// Deep linking configuration
const linking = {
  prefixes: [
    'chessmaps://',
    'https://chessmaps.app',
    'https://www.chessmaps.app',
    'http://localhost:19006',
    'exp://127.0.0.1:19000/--/',
  ],
  config: {
    screens: {
      // Public screens (shown when not logged in)
      Landing: '',           // Root path shows landing page
      Login: 'Login',        // /Login shows login screen
      LoginAlias: 'login',   // /login also shows login screen (lowercase)
      Register: 'register',
      ForgotPassword: 'forgot-password',
      AuthCallback: 'auth/callback',
      SSOCallback: 'sso-callback',  // Clerk OAuth callback route
      LinkAccount: 'link-account',
      Pricing: 'pricing',
      Membership: 'membership',
      // Main screens (shown when logged in)
      Main: {
        screens: {
          MainTabs: {
            screens: {
              Map: 'map',
              Chat: 'chat',
              Tournaments: 'tournaments',
              Clock: 'clock',
              YourClub: 'your-club',
              Profile: 'profile',
            },
          },
          CompleteProfile: 'complete-profile',
          UserDetail: 'user/:userId',
          EventDetails: 'event/:eventId',
          EventCheckIn: 'event/:eventId/check-in',
          CheckInScanner: 'check-in/:eventId',
          AttendanceAnalytics: 'attendance-analytics',
          TournamentDetail: 'tournament/:tournamentId',
          HotspotDetail: 'hotspot/:hotspotId',
          Friends: 'friends',
          Settings: 'settings',
          AdminHotspotsList: 'admin/hotspots',
          AdminHotspotsMap: 'admin/hotspots/map',
          ImportBatches: 'admin/batches',
          AdminClubApprovals: 'admin/club-approvals',
          AdminUsers: 'admin/users',
        },
      },
    },
  },
};

function AuthStack() {
  return (
    <Stack.Navigator screenOptions={{ headerShown: false, cardStyle: { flex: 1 } }}>
      {/* Landing page is the initial screen for unauthenticated users */}
      <Stack.Screen name="Landing" component={LandingPage} />
      {/* Login screens */}
      <Stack.Screen name="Login" component={LoginScreen} />
      <Stack.Screen name="LoginAlias" component={LoginScreen} />
      <Stack.Screen name="Register" component={RegisterScreen} />
      <Stack.Screen name="ForgotPassword" component={ForgotPasswordScreen} />
      <Stack.Screen name="AuthCallback" component={AuthCallbackScreen} />
      <Stack.Screen name="SSOCallback" component={AuthCallbackScreen} />
      <Stack.Screen name="LinkAccount" component={LinkAccountScreen} />
      <Stack.Screen name="Pricing" component={PricingScreen} />
      <Stack.Screen name="Membership" component={MembershipScreen} />
    </Stack.Navigator>
  );
}

// Inject CSS for web to style tab bar internal containers
const injectTabBarStyles = (isDarkMode, isMobileWeb) => {
  if (Platform.OS === 'web' && typeof document !== 'undefined') {
    const styleId = 'tab-bar-dark-mode-styles';
    let styleEl = document.getElementById(styleId);
    
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }
    
    const bgColor = isMobileWeb ? 'transparent' : (isDarkMode ? '#1A1A1A' : '#FFFFFF');
    styleEl.textContent = `
      /* Target the inner tab bar container with flex row */
      [class*="r-flexDirection-18u37iz"][class*="r-flex-13awgt0"] {
        background-color: ${bgColor} !important;
        border: none !important;
      }
      /* Target all potential tab bar containers */
      [data-testid="tabBar"] > div,
      [role="tablist"] {
        background-color: ${bgColor} !important;
        border: none !important;
      }
    `;
  }
};

// Custom tab bar background component
const TabBarBackground = ({ isDarkMode, isMobileWeb }) => {
  // Inject CSS on render
  React.useEffect(() => {
    injectTabBarStyles(isDarkMode, isMobileWeb);
  }, [isDarkMode, isMobileWeb]);

  return (
    <View
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: isMobileWeb ? 'transparent' : (isDarkMode ? '#1A1A1A' : '#FFFFFF'),
      }}
    />
  );
};

function MainTabs({ accountType }) {
  const isClub = accountType === 'CLUB';
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  const shadows = getShadows(isDarkMode);

  const isMobileWeb = Platform.OS === 'web' && isMobile;
  const tabBarBgColor = isMobileWeb ? 'transparent' : (isDarkMode ? '#1A1A1A' : '#FFFFFF');

  return (
    <Tab.Navigator
      key={isDarkMode ? 'dark' : 'light'} // Force re-render when theme changes
      screenOptions={({ route }) => ({
        tabBarIcon: ({ focused, color, size }) => {
          let iconName;

          if (route.name === 'Map') {
            iconName = 'map';
          } else if (route.name === 'Profile') {
            iconName = 'person';
          } else if (route.name === 'Chat') {
            iconName = 'chat';
          } else if (route.name === 'Clock') {
            iconName = 'timer';
          } else if (route.name === 'YourClub') {
            iconName = 'dashboard';
          } else if (route.name === 'Tournaments') {
            iconName = 'emoji-events';
          }

          return (
            <View style={{
              alignItems: 'center',
              justifyContent: 'center',
              paddingTop: isMobile ? 8 : 12,
              marginBottom: isMobile ? 12 : 18,
            }}>
              <View style={{
                backgroundColor: focused ? '#84D288' : 'transparent',
                borderRadius: 10,
                padding: isMobile ? 8 : 10,
                minWidth: isMobile ? 50 : 60,
                alignItems: 'center',
                ...(focused && {
                  elevation: 8,
                  shadowColor: '#76AF87',
                  shadowOffset: {
                    width: 0,
                    height: 4,
                  },
                  shadowOpacity: 1,
                  shadowRadius: 0,
                }),
              }}>
                <Icon 
                  name={iconName} 
                  size={isMobile ? 24 : 26} 
                  color={focused ? (isDarkMode ? '#1A1A1A' : '#FFFFFF') : (isDarkMode ? '#9CA3AF' : '#6C757D')} 
                />
              </View>
            </View>
          );
        },
        tabBarActiveTintColor: colors.primary,
        tabBarInactiveTintColor: colors.tabBarInactive,
        headerShown: false,
        tabBarBackground: () => <TabBarBackground isDarkMode={isDarkMode} isMobileWeb={isMobileWeb} />,
        tabBarStyle: {
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: tabBarBgColor,
          borderTopWidth: 0,
          borderTopColor: 'transparent',
          borderWidth: 0,
          height: isMobile ? 85 : 110,
          paddingBottom: isMobile ? 20 : 35,
          shadowColor: isMobileWeb ? 'transparent' : '#000',
          shadowOffset: { width: 0, height: -4 },
          shadowOpacity: isMobileWeb ? 0 : (isDarkMode ? 0.5 : 0.15),
          shadowRadius: 8,
          elevation: 6,
        },
        // Style the inner content container (the row that holds tab icons)
        tabBarItemStyle: {
          backgroundColor: tabBarBgColor,
          borderWidth: 0,
        },
        tabBarShowLabel: false, // Always hide labels
        tabBarLabelStyle: {
          display: 'none', // Explicitly hide labels
        },
        tabBarLabel: () => null, // Return null for all labels
      })}
    >
      <Tab.Screen name="Map" component={MapScreen} />
      <Tab.Screen name="Chat" component={ChatContainer} />
      <Tab.Screen name="Tournaments" component={TournamentsScreen} />
      {isClub ? (
        <Tab.Screen name="YourClub" component={ClubDashboardScreen} />
      ) : (
        <Tab.Screen name="Clock" component={ChessClockScreen} />
      )}
      <Tab.Screen name="Profile" component={ProfileScreen} />
    </Tab.Navigator>
  );
}

function MainStack() {
  const { user, appUser, authState } = useAuth();
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  const [accountType, setAccountType] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [welcomeAllowAfterTimeout, setWelcomeAllowAfterTimeout] = useState(false);
  const { mapReady } = useMapReady();

  // Show welcome modal when landing in the app right after sign-up (redirect from Complete Profile)
  useEffect(() => {
    if (loading || authState?.needsCompleteProfile || !appUser) return;
    if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
      const showWelcome = sessionStorage.getItem('chessmaps_welcome_toast') === '1';
      if (showWelcome) {
        sessionStorage.removeItem('chessmaps_welcome_toast');
        setShowWelcomeModal(true);
      }
    }
  }, [loading, authState?.needsCompleteProfile, appUser]);

  // Fallback: allow welcome modal after 5s if map hasn’t reported ready (e.g. different tab)
  useEffect(() => {
    if (!showWelcomeModal) return;
    const t = setTimeout(() => setWelcomeAllowAfterTimeout(true), 5000);
    return () => clearTimeout(t);
  }, [showWelcomeModal]);

  const showWelcomeNow = showWelcomeModal && (mapReady || welcomeAllowAfterTimeout);

  useEffect(() => {
    // Use appUser from AuthContext (Supabase) instead of fetching from GraphQL
    if (appUser?.accountType) {
      setAccountType(appUser.accountType);
      setLoading(false);
    } else if (!user) {
      // User not logged in
      setLoading(false);
    } else {
      // User logged in but appUser not loaded yet - wait a bit
      const timer = setTimeout(() => {
        if (!appUser?.accountType) {
          console.warn('⚠️ Account type not available yet, using default');
          setLoading(false);
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [user, appUser]);

  if (loading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: colors.background }}>
        <LoadingAnimation size={525} />
      </View>
    );
  }

  // Check if user needs to complete profile
  if (authState?.needsCompleteProfile) {
    return (
      <Stack.Navigator screenOptions={{ headerShown: false }}>
        <Stack.Screen name="CompleteProfile" component={CompleteProfileScreen} />
      </Stack.Navigator>
    );
  }

  return (
    <MapReadyProvider>
      <Stack.Navigator
        screenOptions={{
          headerShown: false,
          cardStyle: { flex: 1 },
        }}
      >
        <Stack.Screen name="MainTabs">
          {(props) => <MainTabs {...props} accountType={accountType} />}
        </Stack.Screen>
      <Stack.Screen 
        name="UserDetail" 
        component={UserDetailScreen}
        options={{ 
          headerShown: true,
          headerTitle: 'Profile',
          headerBackTitleVisible: false,
        }}
      />
      <Stack.Screen 
        name="CreateEvent" 
        component={CreateEventScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="SocialDraftReview" 
        component={SocialDraftReviewScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="EditRecurringEvent" 
        component={EditRecurringEventScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="ManageEvent" 
        component={ManageEventScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="ManageHotspots" 
        component={ManageHotspotsScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="AddEditHotspot" 
        component={AddEditHotspotScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="MapPicker" 
        component={MapPickerScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="BulkImportHotspots" 
        component={BulkImportHotspotsScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="EventDetails" 
        component={EventDetailsScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="EventDiscovery" 
        component={EventDiscoveryScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="EventCheckIn" 
        component={EventCheckInScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="CheckInScanner" 
        component={CheckInScannerScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="AttendanceAnalytics" 
        component={AttendanceAnalyticsScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="UnclaimedClubs" 
        component={UnclaimedClubsScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="UpgradeClub" 
        component={UpgradeClubScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="TournamentWizard" 
        component={TournamentWizardScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="TournamentDetail" 
        component={TournamentDetailScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="TournamentControl" 
        component={TournamentControlScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="TournamentPlayer" 
        component={TournamentPlayerScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="TournamentProjector" 
        component={TournamentProjectorScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="Settings" 
        component={SettingsScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="CompleteProfile" 
        component={CompleteProfileScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="Pricing" 
        component={PricingScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="Membership" 
        component={MembershipScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="HotspotDetail" 
        component={HotspotDetailScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="Friends" 
        component={FriendsScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="AdminHotspotsList" 
        component={AdminHotspotsListScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="AdminHotspotsMap" 
        component={AdminHotspotsMapScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="ImportBatches" 
        component={ImportBatchesScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="AdminClubApprovals" 
        component={AdminClubApprovalsScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="AdminUsers" 
        component={AdminUsersScreen}
        options={{ headerShown: false }}
      />
      </Stack.Navigator>

      <Modal visible={showWelcomeNow} transparent animationType="fade">
        <View style={welcomeModalStyles.overlay}>
          <Pressable style={welcomeModalStyles.overlayTouchable} onPress={() => setShowWelcomeModal(false)} />
          <View style={[welcomeModalStyles.card, { backgroundColor: colors.card }]}>
            <Text style={[welcomeModalStyles.title, { color: colors.text }]}>Welcome to ChessMaps!</Text>
            <Text style={[welcomeModalStyles.message, { color: colors.textSecondary }]}>
              Your account is all set. Explore the map, find clubs and events, and connect with players near you.
            </Text>
            <Pressable
              style={({ pressed }) => [welcomeModalStyles.button, { backgroundColor: colors.primary, opacity: pressed ? 0.9 : 1 }]}
              onPress={() => setShowWelcomeModal(false)}
            >
              <Text style={welcomeModalStyles.buttonText}>Get started</Text>
            </Pressable>
          </View>
        </View>
      </Modal>
    </MapReadyProvider>
  );
}

const welcomeModalStyles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  overlayTouchable: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.55)',
  },
  card: {
    width: '100%',
    maxWidth: 400,
    borderRadius: 16,
    padding: 28,
    alignItems: 'center',
    zIndex: 1,
    ...getShadows().medium,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    marginBottom: 12,
    textAlign: 'center',
  },
  message: {
    fontSize: 16,
    lineHeight: 24,
    textAlign: 'center',
    marginBottom: 24,
  },
  button: {
    paddingVertical: 14,
    paddingHorizontal: 28,
    borderRadius: 12,
    minWidth: 160,
    alignItems: 'center',
  },
  buttonText: {
    color: '#1A1A1A',
    fontSize: 16,
    fontWeight: '700',
  },
});

function AppNavigator() {
  const { user, loading: authLoading, authState } = useAuth();
  const [showSplash, setShowSplash] = useState(true);
  const [splashMinTimePassed, setSplashMinTimePassed] = useState(false);

  useEffect(() => {
    // Ensure splash screen shows for at least SPLASH_DURATION
    const timer = setTimeout(() => {
      setSplashMinTimePassed(true);
    }, SPLASH_DURATION);

    return () => clearTimeout(timer);
  }, []);

  // Hide splash only after both: min time passed AND auth check complete
  useEffect(() => {
    if (splashMinTimePassed && !authLoading) {
      // Small delay to ensure smooth transition
      const hideTimer = setTimeout(() => {
        setShowSplash(false);
        if (Platform.OS !== 'web') {
          SplashScreen.hideAsync().catch(() => {});
        }
      }, 100);
      return () => clearTimeout(hideTimer);
    }
  }, [splashMinTimePassed, authLoading]);

  const handleSplashFinish = () => {
    // This is called by the splash screen animation
    // We don't immediately hide here - let the useEffect handle it
    // based on both splash duration and auth loading state
  };

  // Show MainStack if user exists OR if we need to complete profile (OAuth sign-up in progress)
  // This allows CompleteProfileScreen to be shown even when session isn't fully activated yet
  const shouldShowMainStack = user || (authState?.needsCompleteProfile && !authLoading);

  // Force remount when switching from Complete Profile → main app so navigation updates reliably
  const mainStackKey = authState?.needsCompleteProfile ? 'complete' : 'main';

  return (
    <>
      <NavigationContainer
        ref={navigationRef}
        linking={linking}
        documentTitle={{
          formatter: (options, route) => {
            const screenName = options?.title ?? route?.name ?? 'Map';
            return `ChessMaps - ${screenName}`;
          },
        }}
      >
        <ChatNotificationProvider navigationRef={navigationRef}>
          {shouldShowMainStack ? <MainStack key={mainStackKey} /> : <AuthStack />}
        </ChatNotificationProvider>
      </NavigationContainer>
      {showSplash && (
        <AppSplashScreen 
          onFinish={handleSplashFinish} 
          duration={SPLASH_DURATION}
        />
      )}
    </>
  );
}

export default AppNavigator;
